import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from '@liminal/web-ui/feedback/useSnackbar';
import { useSortState } from '@liminal/web-ui/primitives/useTableSort';

import { adminClient } from '@/lib/adminClient';
import { buildUsersCsv, type ExportedUserRow } from '@/lib/csv';
import { useUsers } from '@/hooks/useUsers';
import { useUrlState } from '@/hooks/useUrlState';
import { useDebounce } from '@/hooks/useDebounce';
import type { EnrichedProfile } from '@/api/types';
import { AdminSortDirection, AdminUserSortField } from '@/api/generated';

/**
 * Rows per page. The screen's pagination controls step by this too, so the two
 * must never diverge — a fetch size larger than the step shows rows the "Next"
 * button then skips back over.
 */
export const USERS_PAGE_SIZE = 25;

/** The filters and page offset, as they appear in the URL. */
const FILTER_DEFAULTS = { q: '', kyc: '', platform: '', offset: 0 };

/**
 * The sortable columns, and the field each one asks the server for.
 *
 * **The server does this sort, not the console.** admin_api already holds every
 * matching user in memory to count them, so it orders the whole set before
 * cutting the page — which is what makes "oldest first" mean the oldest user
 * rather than the oldest of the twenty-five on screen. Sorting the response
 * here instead was the bug this replaced.
 *
 * KYC is absent on purpose: it is enrichment on the search response, not a
 * profile field, so the server has nothing to order it by. Avatar colour is a
 * swatch.
 */
const SORT_FIELDS = {
  userId: AdminUserSortField.ADMIN_USER_SORT_FIELD_USER_ID,
  email: AdminUserSortField.ADMIN_USER_SORT_FIELD_EMAIL,
  firstName: AdminUserSortField.ADMIN_USER_SORT_FIELD_FIRST_NAME,
  lastName: AdminUserSortField.ADMIN_USER_SORT_FIELD_LAST_NAME,
  platforms: AdminUserSortField.ADMIN_USER_SORT_FIELD_PLATFORMS,
  createdAt: AdminUserSortField.ADMIN_USER_SORT_FIELD_CREATED_AT,
  isOnboardingCompleted: AdminUserSortField.ADMIN_USER_SORT_FIELD_ONBOARDING,
} as const;

export type UserSortColumn = keyof typeof SORT_FIELDS;

const SORT_DIRECTIONS = {
  asc: AdminSortDirection.ADMIN_SORT_DIRECTION_ASC,
  desc: AdminSortDirection.ADMIN_SORT_DIRECTION_DESC,
} as const;

/**
 * All state, effects and handlers for the users list, so the screen is a render
 * switch over this and nothing else.
 *
 * The return is flat and keeps the screen's original names, so extracting this
 * was a move rather than a rewrite of ~350 lines of JSX that has no test behind
 * it. Three filters share one rule and that is why they are exposed as dedicated
 * setters rather than raw ones: changing any filter must also reset paging AND
 * clear the selection, because a selected id on page 4 of the old filter is
 * meaningless under the new one — and it would still be exported. The screen
 * previously passed a setter into a generic `handleFilterChange`, which worked
 * but let a caller reach for the raw setter and skip the reset.
 */
export function useUsersPage() {
  const navigate = useNavigate();

  const [params, setParams] = useUrlState(FILTER_DEFAULTS);
  const { q: query, kyc: kycStatusFilter, platform: platformFilter, offset } = params;
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const snackbar = useSnackbar();
  const [isExporting, setIsExporting] = useState(false);

  const sortState = useSortState<UserSortColumn>('createdAt', 'desc');

  // The box and the URL update on every keystroke; the fetch waits for a
  // pause. Without this "luke+test" is nine searches, each with a CORS
  // preflight, which on top of a page load was enough to trip the admin API's
  // Cloud Armor ban (120 requests/min per IP). The previous results stay on
  // screen while typing instead of flashing to skeleton on every key.
  const debouncedQuery = useDebounce(query, 300);

  const { data, isLoading, isError } = useUsers(
    debouncedQuery,
    USERS_PAGE_SIZE,
    offset,
    kycStatusFilter,
    platformFilter,
    SORT_FIELDS[sortState.column ?? 'createdAt'],
    SORT_DIRECTIONS[sortState.direction],
  );

  // Rendered in the order the server returned them. Re-sorting here would undo
  // the point of asking the server to sort.
  const profiles: EnrichedProfile[] = useMemo(() => data?.profiles ?? [], [data]);
  const totalCount = data?.totalCount ?? 0;

  // checkbox + User ID, Email, First Name, Last Name (4) + KYC
  // + Platforms, Avatar Color, Created At, Onboarding (4)
  const columnCount = 1 + 4 + 1 + 4;

  const allPageIds = useMemo(
    () => new Set(profiles.map((ep) => ep.profile?.userId).filter(Boolean) as string[]),
    [profiles],
  );
  const allPageSelected =
    allPageIds.size > 0 && [...allPageIds].every((id) => selectedIds.has(id));
  const somePageSelected = [...allPageIds].some((id) => selectedIds.has(id));

  /**
   * Changing a filter invalidates both the page offset and the selection. The
   * offset goes back to 0 in the same URL write as the filter — two writes
   * would leave one of them behind.
   */
  const applyFilter = useCallback(
    (patch: Partial<typeof FILTER_DEFAULTS>) => {
      setParams({ ...patch, offset: 0 });
      setSelectedIds(new Set());
    },
    [setParams],
  );

  // Re-sorting invalidates a page offset exactly as changing a filter does:
  // the server re-orders the whole set, so row 76 under the old ordering is a
  // different user under the new one — and so is anything selected there.
  const sort = useMemo(
    () => ({
      ...sortState,
      toggle: (column: UserSortColumn) => {
        sortState.toggle(column);
        applyFilter({});
      },
    }),
    [sortState, applyFilter],
  );

  const setQuery = useCallback((value: string) => applyFilter({ q: value }), [applyFilter]);
  const setKycStatusFilter = useCallback(
    (value: string) => applyFilter({ kyc: value }),
    [applyFilter],
  );
  const setPlatformFilter = useCallback(
    (value: string) => applyFilter({ platform: value }),
    [applyFilter],
  );
  const setOffset = useCallback((value: number) => setParams({ offset: value }), [setParams]);

  const toggleAllOnPage = useCallback(() => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const everySelected = allPageIds.size > 0 && [...allPageIds].every((id) => prev.has(id));
      allPageIds.forEach((id) => (everySelected ? next.delete(id) : next.add(id)));
      return next;
    });
  }, [allPageIds]);

  const toggleOne = useCallback((userId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  }, []);

  /** Serialise and hand the browser a download. Revokes the object URL after. */
  const downloadCsv = useCallback((rows: readonly ExportedUserRow[], filename: string) => {
    const blob = new Blob([buildUsersCsv(rows)], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  }, []);

  const exportSelected = useCallback(() => {
    const rows: ExportedUserRow[] = [];
    for (const ep of profiles) {
      const p = ep.profile;
      if (!p?.userId || !p.email || !selectedIds.has(p.userId)) continue;
      rows.push({ email: p.email, firstName: p.firstName ?? '', lastName: p.lastName ?? '' });
    }
    if (rows.length === 0) {
      snackbar.show('No users to export', 'info');
      return;
    }
    downloadCsv(rows, 'selected-users.csv');
    snackbar.show(`Exported ${rows.length} user${rows.length === 1 ? '' : 's'}`);
  }, [profiles, selectedIds, downloadCsv, snackbar]);

  const exportAllFiltered = useCallback(async () => {
    setIsExporting(true);
    try {
      const resp = await adminClient.exportFilteredEmails({
        query: query || undefined,
        kycStatusFilter: kycStatusFilter || undefined,
        platformFilter: platformFilter || undefined,
      });
      const rows = resp.rows ?? [];
      if (rows.length === 0) {
        snackbar.show('No users to export', 'info');
        return;
      }
      downloadCsv(rows, 'filtered-users.csv');
      const total = resp.total ?? rows.length;
      snackbar.show(`Exported ${total} user${total === 1 ? '' : 's'}`);
    } catch {
      snackbar.show('Failed to export users', 'error');
    } finally {
      setIsExporting(false);
    }
  }, [query, kycStatusFilter, platformFilter, downloadCsv, snackbar]);

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  return useMemo(
    () => ({
      navigate,
      query,
      kycStatusFilter,
      platformFilter,
      setQuery,
      setKycStatusFilter,
      setPlatformFilter,
      offset,
      setOffset,
      totalCount,
      profiles,
      sort,
      isLoading,
      isError,
      columnCount,
      selectedIds,
      allPageSelected,
      somePageSelected,
      handleToggleAll: toggleAllOnPage,
      handleToggleOne: toggleOne,
      clearSelection,
      isExporting,
      handleExportSelectedCsv: exportSelected,
      handleExportAllFilteredCsv: exportAllFiltered,
      snackbar,
    }),
    [
      navigate, query, kycStatusFilter, platformFilter, setQuery, setKycStatusFilter,
      setPlatformFilter, offset, setOffset, totalCount, profiles, sort, isLoading, isError, columnCount,
      selectedIds, allPageSelected, somePageSelected, toggleAllOnPage, toggleOne, clearSelection,
      isExporting, exportSelected, exportAllFiltered, snackbar,
    ],
  );
}
